import { groupEventsByUpload } from './FeedList';
import { isShareEvent } from './shareEvents';

const emptyStats = () => ({
  opens: 0,
  sessions: 0,
  shuffles: 0,
  shares: 0,
  downloads: 0,
  lastActivityAt: null,
});

/**
 * Period-scoped engagement stats per layout from the analytics feed.
 * Keys: byUuid and byMongoId Maps; use lookup(plan) from FloorPlanCard.
 *
 * @param {Array} feedRows
 * @param {Set<string>|null} [excludeUsers] optional set of meta.user
 *   values to skip when counting opens/sessions/shuffles/shares/downloads.
 *   Pass the visitorSegregation insider set when the "Include creator
 *   activity" toggle is off. Additive — defaults to null for existing
 *   callers, in which case behavior is unchanged.
 */
export function buildLayoutStatsFromFeed(feedRows, excludeUsers = null) {
  const byUuid = new Map();
  const byMongoId = new Map();
  const rows = Array.isArray(feedRows) ? feedRows : [];
  const groups = groupEventsByUpload(rows);
  const hasExclusions = excludeUsers && excludeUsers.size > 0;

  groups.forEach((group) => {
    const stats = emptyStats();
    const sessionKeys = new Set();

    group.rows.forEach((row) => {
      const ev = row?.meta?.event;
      const u = row?.meta?.user;
      const skipForExclusion = hasExclusions && u && excludeUsers.has(String(u));

      if (!skipForExclusion) {
        if (ev === 'webview_opened') stats.opens += 1;
        if (ev === 'shuffle_room') stats.shuffles += 1;
        if (isShareEvent(ev)) stats.shares += 1;
        if (ev === 'download_layout') stats.downloads += 1;
      }

      const sk =
        row?.meta?.guestSessionId ||
        (row?.meta?.user && row.meta.user !== 'guest' && row.meta.user !== 'anonymous'
          ? row.meta.user
          : null);
      if (
        sk &&
        ev &&
        ev !== 'floorplan_uploaded' &&
        ev !== 'gallery_ready' &&
        !skipForExclusion
      ) {
        sessionKeys.add(String(sk));
      }

      if (row?.timestamp && !skipForExclusion) {
        const t = new Date(row.timestamp).getTime();
        if (Number.isFinite(t)) {
          const prev = stats.lastActivityAt ? new Date(stats.lastActivityAt).getTime() : 0;
          if (t > prev) stats.lastActivityAt = row.timestamp;
        }
      }
    });
    stats.sessions = sessionKeys.size;

    const assign = (key) => {
      if (!key) return;
      const s = String(key);
      if (/^[0-9a-fA-F]{24}$/.test(s)) {
        byMongoId.set(s, stats);
      } else {
        byUuid.set(s, stats);
      }
    };

    assign(group.key);
    group.rows.forEach((row) => {
      assign(row?.detail?.galleryId);
      assign(row?.detail?.uniqueId);
    });
  });

  const lookup = (plan) => {
    if (!plan) return null;
    if (plan.uuid && byUuid.has(plan.uuid)) return byUuid.get(plan.uuid);
    if (plan._id && byMongoId.has(plan._id)) return byMongoId.get(plan._id);
    return null;
  };

  return { byUuid, byMongoId, lookup };
}

export function formatRelativeActivity(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  const diff = Math.max(0, Date.now() - then);
  const min = Math.floor(diff / 60000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
